import type { AlertRuleFilters } from '@detect/shared';

/** The alert kinds a rule can be limited to (#26), in the words a tenant uses. */
export const DETECTION_MODE_OPTIONS = [
  { value: 'outage', label: 'Outage', hint: 'a problem outside any planned change' },
  { value: 'change-expected', label: 'Planned change impact', hint: 'an expected effect while a change window is open' },
  { value: 'change-escaped', label: 'Change impact on a witness', hint: 'a problem on something the change was not meant to touch' },
  { value: 'change-unrecovered', label: 'Change not recovered', hint: 'something is still wrong after a change window ended' },
] as const;

export type DetectionMode = (typeof DETECTION_MODE_OPTIONS)[number]['value'];

const LABELS = new Map<string, string>(DETECTION_MODE_OPTIONS.map((o) => [o.value, o.label]));

/** "All alerts" when a rule is not limited by kind, else the kinds in words. */
export function describeRuleKinds(modes: AlertRuleFilters['detectionModes']): string {
  if (!modes || modes.length === 0) return 'All alerts';
  return modes.map((m) => LABELS.get(m) ?? m).join(', ');
}

/** How many resources a rule is limited to, in words ("Any resource" when it is not). */
export function describeRuleResources(resourceIds: AlertRuleFilters['resourceIds'], names: ReadonlyMap<string, string>): string {
  if (!resourceIds || resourceIds.length === 0) return 'Any resource';
  const shown = resourceIds.slice(0, 3).map((id) => names.get(id) ?? '(removed)');
  const more = resourceIds.length > 3 ? ` and ${resourceIds.length - 3} more` : '';
  return `${shown.join(', ')}${more}`;
}

/**
 * What happens to an alert about a change that did not recover, in the tenant's words. Shown on the alert rule form and on the
 * escalation chains page, so the default is visible where chains and rules are managed (the PO's decision of 2026-10-11).
 */
export const CHANGE_NOT_RECOVERED_DEFAULT =
  'By default, an alert that a change did not recover goes to the person who made the change first. If they have not ' +
  'acknowledged it after 15 minutes, it escalates to the chain of the first rule that would route an ordinary alert for the ' +
  'same resource, and then through that chain\'s own stages. Acknowledging it stops the escalation.';

/** How a rule that names "Change not recovered" changes that. */
export const CHANGE_NOT_RECOVERED_OVERRIDE =
  'A rule with "Change not recovered" ticked replaces that default for the alerts it matches: they go straight to this ' +
  "rule's chain, with that chain's own stages and timings, and nothing is sent to the person who made the change first " +
  'unless the chain includes them. Rules without it do not receive these alerts directly; they are only where the default escalates to.';
